import React from 'react';
import { useCountUp } from '../utils/useCountUp';
import { fromUnits } from '../utils/format';

// Animated USDT amount (takes a BigNumber, counts up the display)
export function CountUpUSDT({ value, dp = 2, className = '' }) {
  const target = Number(fromUnits(value));
  const n = useCountUp(target);
  const text = n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return <span className={'countup ' + className}>{text}</span>;
}

// Animated plain integer
export function CountUpInt({ value, className = '' }) {
  const n = useCountUp(Number(value) || 0, 800);
  return <span className={'countup ' + className}>{Math.round(n)}</span>;
}

// Per-level unlock progress. `levels` = { eligibility, percents, directReqs, memberReqs, memberCounts }
export function LevelProgress({ levels, directReferrals }) {
  if (!levels) return null;
  return (
    <div>
      {[0, 1, 2, 3, 4].map((i) => {
        const unlocked = levels.eligibility[i];
        const dReq = levels.directReqs[i];
        const mReq = levels.memberReqs[i];
        const dHave = directReferrals;
        const mHave = levels.memberCounts[i];

        // progress = min ratio across the two criteria (L1 has none -> full)
        let pct = 100;
        if (i > 0) {
          const dr = dReq ? Math.min(dHave / dReq, 1) : 1;
          const mr = mReq ? Math.min(mHave / mReq, 1) : 1;
          pct = Math.round(Math.min(dr, mr) * 100);
        }

        return (
          <div className="lp-row" key={i}>
            <div className="lp-head">
              <span className="lp-left">
                <span className="lvl-badge">L{i + 1}</span>
                <span className="lp-pctnum">{(levels.percents[i] / 100)}%</span>
              </span>
              <span className={'tag ' + (unlocked ? 'ok' : 'locked')}>
                {unlocked ? 'Unlocked' : `${pct}%`}
              </span>
            </div>
            <div className="lp-track">
              <div className={'lp-fill ' + (unlocked ? 'done' : 'partial')} style={{ width: pct + '%' }} />
            </div>
            {i > 0 && (
              <div className="lp-sub">
                <span>Directs <b>{dHave}/{dReq}</b></span>
                <span>Level members <b>{mHave}/{mReq}</b></span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Team distribution bars across 5 levels
export function TeamStrength({ memberCounts }) {
  if (!memberCounts) return null;
  const max = Math.max(1, ...memberCounts);
  return (
    <div>
      {memberCounts.map((c, i) => (
        <div className="ts-row" key={i}>
          <span className="ts-lbl">L{i + 1}</span>
          <div className="ts-track"><div className="ts-fill" style={{ width: (c / max) * 100 + '%' }} /></div>
          <span className="ts-num">{c}</span>
        </div>
      ))}
    </div>
  );
}

// Weekly earnings sparkline (inline SVG bars). data = [{ label, value }]
export function Sparkline({ data }) {
  if (!data || data.length === 0) {
    return <div className="spark-empty">No income recorded yet — your weekly earnings will appear here.</div>;
  }
  const W = 300, H = 84, gap = 6;
  const max = Math.max(...data.map((d) => d.value), 1);
  const bw = (W - gap * (data.length - 1)) / data.length;
  return (
    <div className="spark">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--gold-bright)" />
            <stop offset="100%" stopColor="var(--blue)" />
          </linearGradient>
        </defs>
        {data.map((d, i) => {
          const h = Math.max(3, (d.value / max) * (H - 8));
          const x = i * (bw + gap);
          const y = H - h;
          return <rect key={i} className="bar" x={x} y={y} width={bw} height={h} rx="3" fill="url(#sparkGrad)" />;
        })}
      </svg>
    </div>
  );
}

// skeleton placeholders
export function DashboardSkeleton() {
  return (
    <div>
      <div className="skel skel-card" />
      <div className="skel skel-card" style={{ height: 90 }} />
      <div className="skel skel-big" />
      <div className="skel skel-card" style={{ height: 70 }} />
    </div>
  );
}
